import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { CheckCircle, Download, Mail, Phone, ArrowRight, FileText, X, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { DOSSIER_PDF_URL, sponsorshipPackages, getKeyFigures, getBenefits, getCommitments } from '../components/partenaires/partenairesData';
import TrustedPartners from '../components/partenaires/TrustedPartners';

const API = `${window.location.origin}/api`;

export default function Partenaires() {
  const { t } = useTranslation();
  const benefits = getBenefits(t);
  const commitments = getCommitments(t);
  const keyFigures = getKeyFigures(t);

  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', company: '', phone: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSelectPackage = (pkgName) => {
    setSelectedPackage(pkgName);
    setShowContactModal(true);
    setContactForm({ ...contactForm, message: `Je suis intéressé(e) par le Pack ${pkgName} et souhaite en savoir plus.` });
  };

  const handleSubmitContact = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: contactForm.name, email: contactForm.email,
        subject: `Demande de partenariat - Pack ${selectedPackage} - ${contactForm.company}`,
        message: `Entreprise: ${contactForm.company}\nTéléphone: ${contactForm.phone}\n\n${contactForm.message}`
      });
      toast.success(t('contactExt.successMsg'), { description: t('contactExt.successDesc') });
      setSubmitted(true);
    } catch (error) {
      toast.error(t('contactExt.errorMsg'), { description: t('contactExt.errorDesc') });
    } finally { setLoading(false); }
  };

  const closeModal = () => {
    setShowContactModal(false); setSelectedPackage(null); setSubmitted(false);
    setContactForm({ name: '', email: '', company: '', phone: '', message: '' });
  };

  return (
    <div className="min-h-screen pt-24 bg-white" data-testid="partenaires-page">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-fuchsia-600 via-fuchsia-700 to-purple-800 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-lime-400 rounded-full blur-3xl opacity-20" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-fuchsia-300 rounded-full blur-3xl opacity-20" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="inline-block px-4 py-2 bg-white/20 rounded-full text-sm font-medium uppercase tracking-widest mb-6">{t('partners.becomePartner')}</span>
          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl font-bold mb-6">{t('partners.supportUs')}</h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto mb-10">{t('partners.heroDesc')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={DOSSIER_PDF_URL} target="_blank" rel="noopener noreferrer"><Button className="bg-white text-fuchsia-600 hover:bg-fuchsia-50 rounded-full px-8 py-6 text-lg" data-testid="download-dossier-btn"><Download className="mr-2 h-5 w-5" />{t('partners.downloadDossier')}</Button></a>
            <Link to="/contact"><Button variant="outline" className="border-2 border-white/40 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg" data-testid="contact-partenariat-btn">{t('partners.contactUs')}</Button></Link>
          </div>
        </div>
      </section>

      {/* Key Figures */}
      <section className="py-12 bg-zinc-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {keyFigures.map((figure, index) => (
              <div key={index} className="text-center" data-testid={`key-figure-${index}`}>
                <figure.icon className="h-8 w-8 mx-auto mb-3 text-fuchsia-400" />
                <p className="text-4xl font-bold text-white mb-1">{figure.value}</p>
                <p className="text-zinc-400">{figure.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Partner */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-lime-100 text-lime-600 rounded-full text-sm font-medium uppercase tracking-widest mb-4">{t('partners.whyJoinUs')}</span>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-zinc-900 mb-6">{t('partners.partnershipBenefits')}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <Card key={index} className="border-0 shadow-lg hover:-translate-y-2 transition-all duration-300 bg-gradient-to-br from-white to-fuchsia-50/30" data-testid={`benefit-${index}`}>
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-gradient-to-br from-fuchsia-500 to-lime-500 flex items-center justify-center"><benefit.icon className="h-7 w-7 text-white" /></div>
                  <h3 className="font-bold text-zinc-900 mb-2">{benefit.title}</h3>
                  <p className="text-sm text-zinc-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="py-20 bg-zinc-50" data-testid="packages-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-fuchsia-100 text-fuchsia-600 rounded-full text-sm font-medium uppercase tracking-widest mb-4">{t('partners.ourFormulas')}</span>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-zinc-900 mb-6">{t('partners.choosePackage')}</h2>
            <p className="text-zinc-600 max-w-2xl mx-auto">{t('partners.packagesDesc')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {sponsorshipPackages.map((pkg, index) => (
              <Card key={index} className={`border-2 ${pkg.borderColor} ${pkg.bgColor} hover:-translate-y-2 transition-all duration-300 overflow-hidden`} data-testid={`package-${pkg.name.toLowerCase()}`}>
                <CardHeader className={`bg-gradient-to-r ${pkg.color} text-white p-6`}>
                  <div className="flex items-center justify-between">
                    <div><CardTitle className="text-2xl font-bold">Pack {pkg.name}</CardTitle><p className="text-white/90 text-3xl font-bold mt-2">{pkg.price}</p></div>
                    <pkg.icon className="h-12 w-12 text-white/80" />
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <ul className="space-y-3">
                    {pkg.features.map((feature, fIndex) => (<li key={fIndex} className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-lime-500 flex-shrink-0 mt-0.5" /><span className="text-sm text-zinc-700">{feature}</span></li>))}
                  </ul>
                  <Button className={`w-full mt-6 bg-gradient-to-r ${pkg.color} text-white hover:opacity-90 rounded-full`} onClick={() => handleSelectPackage(pkg.name)} data-testid={`select-${pkg.name.toLowerCase()}`}>{t('partners.selectPack')}</Button>
                </CardContent>
              </Card>
            ))}
          </div>
          <Card className="mt-12 border-2 border-dashed border-fuchsia-300 bg-fuchsia-50/50" data-testid="technical-partnership">
            <CardContent className="p-8 text-center">
              <h3 className="font-['Playfair_Display'] text-2xl font-bold text-zinc-900 mb-4">{t('partners.technicalPartnership')}</h3>
              <p className="text-zinc-600 mb-6 max-w-2xl mx-auto">{t('partners.technicalDesc')}</p>
              <Button variant="outline" className="rounded-full border-fuchsia-500 text-fuchsia-600 hover:bg-fuchsia-50" onClick={() => { setSelectedPackage("Sur Mesure"); setShowContactModal(true); setContactForm({ ...contactForm, message: "Je souhaite discuter d'un partenariat technique ou mécénat sur mesure." }); }}>
                {t('partners.discussProject')}<ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Commitments */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-lime-100 text-lime-600 rounded-full text-sm font-medium uppercase tracking-widest mb-4">{t('partners.ourCommitment')}</span>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-zinc-900 mb-6">{t('partners.whatWeGuarantee')}</h2>
              <ul className="space-y-4">
                {commitments.map((commitment, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-r from-fuchsia-500 to-lime-500 flex items-center justify-center flex-shrink-0"><CheckCircle className="h-4 w-4 text-white" /></div>
                    <span className="text-zinc-700">{commitment}</span>
                  </li>
                ))}
              </ul>
            </div>
            <Card className="border-0 shadow-2xl overflow-hidden">
              <CardContent className="p-0">
                <div className="bg-gradient-to-br from-fuchsia-600 to-purple-700 p-8 text-white">
                  <FileText className="h-12 w-12 mb-4" />
                  <h3 className="text-2xl font-bold mb-2">{t('partners.sponsorDossier')}</h3>
                  <p className="text-white/90 mb-6">{t('partners.dossierDesc')}</p>
                  <a href={DOSSIER_PDF_URL} target="_blank" rel="noopener noreferrer"><Button className="bg-white text-fuchsia-600 hover:bg-fuchsia-50 rounded-full w-full"><Download className="mr-2 h-4 w-4" />{t('partners.downloadPdf')}</Button></a>
                </div>
                <div className="p-6 bg-white">
                  <p className="text-sm text-zinc-600 mb-4"><strong>{t('partners.questions')}</strong> {t('partners.questionsDesc')}</p>
                  <div className="flex flex-col gap-2">
                    <Link to="/contact" className="flex items-center gap-2 text-fuchsia-600 hover:text-fuchsia-700"><Mail className="h-4 w-4" />Nous contacter</Link>
                    <a href="tel:+33749513090" className="flex items-center gap-2 text-fuchsia-600 hover:text-fuchsia-700"><Phone className="h-4 w-4" />07 49 51 30 90</a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <TrustedPartners />

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-lime-500 to-lime-600" data-testid="partenaires-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center text-white">
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold mb-6">{t('partners.readyToJoin')}</h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">{t('partners.ctaDesc')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact"><Button className="bg-white text-lime-600 hover:bg-lime-50 rounded-full px-10 py-6 text-lg font-medium" data-testid="cta-contact-btn">{t('partners.contactUs')}<ArrowRight className="ml-2 h-5 w-5" /></Button></Link>
            <a href={DOSSIER_PDF_URL} target="_blank" rel="noopener noreferrer"><Button variant="outline" className="border-2 border-white/40 text-white hover:bg-white/10 rounded-full px-10 py-6 text-lg"><Download className="mr-2 h-5 w-5" />{t('partners.downloadDossier').split(' ').slice(0, 2).join(' ')}</Button></a>
          </div>
        </div>
      </section>

      {/* Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeModal} data-testid="contact-modal">
          <Card className="max-w-lg w-full border-0 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="bg-gradient-to-r from-fuchsia-600 to-purple-700 text-white relative">
              <Button variant="ghost" size="icon" className="absolute top-4 right-4 text-white hover:bg-white/20" onClick={closeModal}><X className="h-5 w-5" /></Button>
              <CardTitle className="flex items-center gap-2 pr-8"><Mail className="h-5 w-5" />{t('partners.partnershipRequest')}</CardTitle>
              <p className="text-white/90 text-sm mt-2">{t('partners.selectedPack')} : <strong>{selectedPackage}</strong></p>
            </CardHeader>
            <CardContent className="p-6">
              {submitted ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-16 w-16 text-lime-500 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-zinc-900 mb-2">{t('partners.thankYouInterest')}</h3>
                  <p className="text-zinc-600 mb-4">{t('partners.requestSent')}</p>
                  <Button onClick={closeModal} variant="outline" className="rounded-full">{t('common.close')}</Button>
                </div>
              ) : (
                <form onSubmit={handleSubmitContact} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label htmlFor="contact-name">{t('partners.fullName')} *</Label><Input id="contact-name" value={contactForm.name} onChange={(e) => setContactForm({...contactForm, name: e.target.value})} required placeholder="Jean Dupont" data-testid="partner-contact-name" /></div>
                    <div><Label htmlFor="contact-company">{t('partners.company')} *</Label><Input id="contact-company" value={contactForm.company} onChange={(e) => setContactForm({...contactForm, company: e.target.value})} required placeholder="Ma Société" data-testid="partner-contact-company" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label htmlFor="contact-email">Email *</Label><Input id="contact-email" type="email" value={contactForm.email} onChange={(e) => setContactForm({...contactForm, email: e.target.value})} required placeholder="email@exemple.com" data-testid="partner-contact-email" /></div>
                    <div><Label htmlFor="contact-phone">{t('partners.phone')}</Label><Input id="contact-phone" value={contactForm.phone} onChange={(e) => setContactForm({...contactForm, phone: e.target.value})} placeholder="06 12 34 56 78" data-testid="partner-contact-phone" /></div>
                  </div>
                  <div><Label htmlFor="contact-message">Message</Label><Textarea id="contact-message" rows={4} value={contactForm.message} onChange={(e) => setContactForm({...contactForm, message: e.target.value})} placeholder="Décrivez votre projet de partenariat..." data-testid="partner-contact-message" /></div>
                  <div className="flex gap-3">
                    <Button type="submit" className="flex-1 bg-fuchsia-600 hover:bg-fuchsia-700 text-white rounded-full" disabled={loading} data-testid="partner-contact-submit">{loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}{t('partners.sendRequest')}</Button>
                    <Button type="button" variant="outline" onClick={closeModal} className="rounded-full">{t('common.cancel')}</Button>
                  </div>
                  <p className="text-xs text-zinc-500 text-center">{t('partners.formConsent')}</p>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
